import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const IDLE_THRESHOLD_MS = 10 * 60 * 1000;
const RATE_LIMIT_BACKOFF_MS = 60 * 1000;
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'keydown',
  'pointerdown',
  'scroll',
  'touchstart',
];

interface RecordUserActivityResponse {
  success: boolean;
  sessionId?: string;
  reused?: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserActivityTrackingService {
  private sessionId?: string;
  private startPromise?: Promise<void>;
  private heartbeatInterval?: ReturnType<typeof setInterval>;
  private heartbeatPromise?: Promise<void>;
  private lastInteractionAt = Date.now();
  private lastHeartbeatAt = Date.now();
  private heartbeatBackoffUntil = 0;

  constructor(
    private functions: Functions,
  ) {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      ACTIVITY_EVENTS.forEach(eventName => {
        window.addEventListener(eventName, this.onUserInteraction, { passive: true });
      });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  start(): Promise<void> {
    if (this.sessionId) {
      return Promise.resolve();
    }

    if (!this.startPromise) {
      this.startPromise = this.startSession().finally(() => {
        this.startPromise = undefined;
      });
    }

    return this.startPromise;
  }

  private async startSession(): Promise<void> {
    try {
      const fn = httpsCallable<{ action: 'start' }, RecordUserActivityResponse>(this.functions, 'recordUserActivity');
      const result = await fn({ action: 'start' });

      if (!result.data.success || !result.data.sessionId) {
        return;
      }

      this.sessionId = result.data.sessionId;
      this.lastHeartbeatAt = Date.now();
      if (!this.heartbeatInterval) {
        this.heartbeatInterval = setInterval(() => {
          void this.heartbeat();
        }, HEARTBEAT_INTERVAL_MS);
      }
    } catch (e) {
      console.error('[UserActivityTrackingService] Failed to start activity session:', e);
    }
  }

  stop(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }

    if (!this.sessionId) {
      return;
    }

    const sessionId = this.sessionId;
    this.sessionId = undefined;
    const activeMs = this.getActiveMsSinceHeartbeat();
    this.lastHeartbeatAt = Date.now();

    const fn = httpsCallable<{ action: 'end'; sessionId: string; activeMs: number }, RecordUserActivityResponse>(this.functions, 'recordUserActivity');
    fn({ action: 'end', sessionId, activeMs }).catch(e => {
      console.error('[UserActivityTrackingService] Failed to end activity session:', e);
    });
  }

  private heartbeat(force = false, requestedActiveMs?: number): Promise<void> {
    if (!this.sessionId || (!force && !this.isActivelyEngaged())) {
      return Promise.resolve();
    }

    if (Date.now() < this.heartbeatBackoffUntil) {
      return Promise.resolve();
    }

    if (this.heartbeatPromise) {
      return this.heartbeatPromise;
    }

    const sessionId = this.sessionId;
    const activeMs = requestedActiveMs ?? this.getActiveMsSinceHeartbeat();
    const sentAt = Date.now();
    const fn = httpsCallable<
      { action: 'heartbeat'; sessionId: string; activeMs: number },
      RecordUserActivityResponse
    >(this.functions, 'recordUserActivity');

    this.heartbeatPromise = fn({ action: 'heartbeat', sessionId, activeMs })
      .then(result => {
        if (result.data.success) {
          this.lastHeartbeatAt = sentAt;
          return;
        }

        if (['Session ended', 'Session expired', 'Session superseded', 'Session not found'].includes(result.data.error ?? '')) {
          this.sessionId = undefined;
          this.lastHeartbeatAt = Date.now();
          if (this.isActivelyEngaged()) {
            void this.start();
          }
        }
      })
      .catch(e => {
        if (this.isRateLimitError(e)) {
          this.heartbeatBackoffUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
          console.warn('[UserActivityTrackingService] Activity heartbeat rate-limited; backing off briefly.');
          return;
        }

        console.error('[UserActivityTrackingService] Failed to send activity heartbeat:', e);
      })
      .finally(() => {
        this.heartbeatPromise = undefined;
      });

    return this.heartbeatPromise;
  }

  private readonly onUserInteraction = (): void => {
    const wasIdle = Date.now() - this.lastInteractionAt > IDLE_THRESHOLD_MS;
    this.lastInteractionAt = Date.now();

    if (wasIdle && !this.isDocumentHidden()) {
      this.lastHeartbeatAt = Date.now();
      void this.heartbeat(true, 0);
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (this.isDocumentHidden()) {
      void this.heartbeat(true, this.getActiveMsSinceHeartbeat(true));
      return;
    }

    this.lastInteractionAt = Date.now();
    this.lastHeartbeatAt = Date.now();
    void this.heartbeat(true, 0);
  };

  private isActivelyEngaged(): boolean {
    return !this.isDocumentHidden()
      && Date.now() - this.lastInteractionAt <= IDLE_THRESHOLD_MS;
  }

  private isDocumentHidden(): boolean {
    return typeof document !== 'undefined' && document.hidden;
  }

  private getActiveMsSinceHeartbeat(ignoreVisibility = false): number {
    const recentlyActive = Date.now() - this.lastInteractionAt <= IDLE_THRESHOLD_MS;
    if (!recentlyActive || (!ignoreVisibility && this.isDocumentHidden())) {
      return 0;
    }

    return Math.max(0, Math.min(
      Date.now() - this.lastHeartbeatAt,
      HEARTBEAT_INTERVAL_MS,
    ));
  }

  private isRateLimitError(error: unknown): boolean {
    const code = (error as { code?: unknown })?.code;
    const message = (error as { message?: unknown })?.message;

    return code === 'resource-exhausted'
      || code === 'functions/resource-exhausted'
      || (typeof message === 'string' && message.includes('requests too quickly'));
  }
}
